import ApiService from './api.service';

class MessageError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

const MessageService = {
  /**
   * Send message.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  send: async function(data) {
    try {
      const response = await ApiService.post('/messages/send', data);

      //ApiService.mount401Interceptor();
      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Save message as draft.
   *
   * @returns Draft
   * @throws MessageError
   **/
  saveAsDraft: async function(data) {
    try {
      const response = await ApiService.post('/messages/draft', data);

      //ApiService.mount401Interceptor();
      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Schedule message.
   *
   * @returns Schedule
   * @throws MessageError
   **/
  schedule: async function(data) {
    try {
      const response = await ApiService.post('messages/schedule', data);

      //ApiService.mount401Interceptor();
      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch current user's outbox messages.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  outboxFetchAll: async function(page) {
    try {
      const response = await ApiService.get('/messages/outbox?page=' + page);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch current user's draft messages.
   *
   * @returns Drafts
   * @throws MessageError
   **/
  draftFetchAll: async function(page) {
    try {
      const response = await ApiService.get('/messages/draft?page=' + page);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch current user's schedule messages.
   *
   * @returns Schedules
   * @throws MessageError
   **/
  scheduleFetchAll: async function(page) {
    try {
      const response = await ApiService.get('/messages/schedule?page=' + page);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete sent message by id.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  deleteSent: async function(id) {
    try {
      const response = await ApiService.delete('/messages/outbox/' + id, {});

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch sent message by id.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  fetchSent: async function(id) {
    try {
      const response = await ApiService.get('/messages/outbox/' + id);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch sent message extras by sent id.
   *
   * @returns SentExtra
   * @throws MessageError
   **/
  fetchSentExtra: async function(id) {
    try {
      const response = await ApiService.get(
        '/messages/outbox/' + id + '/extra'
      );

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Resend message by id.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  resend: async function(id) {
    try {
      const response = await ApiService.patch('/messages/' + id + '/resend');

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete multi sent messages by ids.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  deleteMultipleSent: async function(ids) {
    try {
      const response = await ApiService.delete('/messages/outbox', {
        ids: ids,
      });

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete schedule message by id.
   *
   * @returns Schedule
   * @throws MessageError
   **/
  deleteSchedule: async function(id) {
    try {
      const response = await ApiService.delete('/messages/schedule/' + id, {});

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch schedule message by id.
   *
   * @returns Schedule
   * @throws MessageError
   **/
  fetchSchedule: async function(id) {
    try {
      const response = await ApiService.get('/messages/schedule/' + id);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete multi scheduled messages by ids.
   *
   * @returns Schedule
   * @throws MessageError
   **/
  deleteMultipleSchedules: async function(ids) {
    try {
      const response = await ApiService.delete('/messages/schedule', {
        ids: ids,
      });

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete draft message by id.
   *
   * @returns Draft
   * @throws MessageError
   **/
  deleteDraft: async function(id) {
    try {
      const response = await ApiService.delete('/messages/draft/' + id, {});

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch draft message by id.
   *
   * @returns Draft
   * @throws MessageError
   **/
  fetchDraft: async function(id) {
    try {
      const response = await ApiService.get('/messages/draft/' + id);

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Delete multi draft messages by ids.
   *
   * @returns Draft
   * @throws MessageError
   **/
  deleteMultipleDrafts: async function(ids) {
    try {
      const response = await ApiService.delete('/messages/draft', { ids: ids });

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Send draft message by id.
   *
   * @returns Outbox
   * @throws MessageError
   **/
  sendDraft: async function(id) {
    try {
      const response = await ApiService.patch(
        '/messages/draft/' + id + '/send'
      );

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * toggle scheduled message by id.
   *
   * @returns response
   * @throws MessageError
   **/
  toggleSchedule: async function(id) {
    try {
      const response = await ApiService.patch(
        '/messages/schedule/' + id + '/toggle'
      );

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },

  /**
   * Fetch the user names requested by the user.
   *
   * @returns sender names
   * @throws MessageError
   **/

  fetchSenderNames: async function() {
    try {
      const response = await ApiService.get('users/sender-names');

      return response.data;
    } catch (error) {
      throw new MessageError(error.response.status, error.response.data.detail);
    }
  },
};

export { MessageService, MessageError };
